/**
 * PageAdminPanel Component
 *
 * DEV_TEAM_ADMIN-only "Admin" view of the Page side panel. Lets an admin pin the `getPageLayout`
 * anchor overrides — deck cycle, publish year, as-of date — so the page renders as that cycle / year /
 * date would resolve it, without waiting for the deck to actually reach that cycle. Surfaces the
 * server's `resolvedDeckCycle` and `resolvedTemplateCycle` so the admin can tell a cycle-specific
 * layout from the `_` default quietly answering for it.
 *
 * The pinned overrides live in PageContext (so the page-layout fetch in `Page.tsx` picks them up); this
 * panel is the control surface. It reads the resolved fields back off `useGetPageLayout` with the same
 * params, which is a React Query cache hit on the fetch `Page.tsx` already issued.
 */

import './PageAdminPanel.scss';

import { usePageContext } from '@context/PageContext';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useGetPageLayout } from '@services/api/deckManagement/hooks/useGetPageLayout';
import { useScenarioManagementEntries } from '@services/api/deckManagement/hooks/useScenarioManagement';
import { entityKeyAsId } from '@utils/deckHelpers';
import { isNil, uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageAdminPanelProps, PageLayoutOverrides } from 'types/PageSidePanel.types';

/**
 * Trims input and returns undefined for an empty string, so a blank field clears the override rather
 * than pinning an empty value.
 * @param value - The raw input value
 */
const normalizeField = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

/**
 * Sorts deck cycles for the picker using natural (numeric-aware) collation, so embedded numbers order
 * correctly (Day7 before Day12, GQ1 before GQ2) across every cycle shape — Day#, GQ#, and OP-style
 * (OP1_Prelim_Supplemental) — with a consistent, transitive ordering even when a list mixes shapes.
 * @param a - First cycle
 * @param b - Second cycle
 */
const sortDeckCycles = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true });

// Publish-year picker window: two years back through five years ahead (forward-weighted, since the
// primary use is previewing an upcoming cycle's year).
const PUBLISH_YEAR_PAST = 2;
const PUBLISH_YEAR_FUTURE = 5;

// Sentinel for the Publish Year picker's default option: send NO publish_year override, so the BE
// uses the deck's own plan year. publish_year only affects GQ# guidance cycles server-side; leaving it
// unset avoids overriding the deck's real year on unrelated previews.
const DECK_DEFAULT_YEAR = '';

/**
 * Builds the publish-year options: currentYear-2 .. currentYear+5, ascending, as strings to match the
 * getPageLayout `publish_year` argument.
 */
const buildPublishYearOptions = (): string[] => {
  const current = new Date().getFullYear();
  const years: string[] = [];
  for (let year = current - PUBLISH_YEAR_PAST; year <= current + PUBLISH_YEAR_FUTURE; year += 1) {
    years.push(String(year));
  }
  return years;
};

const PageAdminPanel = ({ overrides, onOverridesChange }: PageAdminPanelProps) => {
  const { currentGranularityPage } = usePageContext();
  const { deckId } = useParams<{ deckId: string }>();

  // Publish Year defaults to "deck default" (DECK_DEFAULT_YEAR sentinel → send nothing, so the BE uses
  // the deck's own plan year). The picker offers a bounded window of explicit years to override it.
  const publishYearOptions = useMemo(() => buildPublishYearOptions(), []);

  // Known deck cycles for the current deck, from its scenario entries (same source ScenarioManagement
  // uses). Cache-shared React Query read; suggestions only — the field stays freeSolo since valid
  // cycles (e.g. OP1_Prelim_Supplemental) may not appear here and getPageLayout does not validate them.
  const { data: scenarioEntries } = useScenarioManagementEntries(deckId ?? null, {
    enabled: !isNil(deckId),
  });
  const deckCycleOptions = useMemo(
    () =>
      uniq((scenarioEntries ?? []).map((entry) => entry.deck_cycle).filter((cycle): cycle is string => !!cycle)).sort(
        sortDeckCycles,
      ),
    [scenarioEntries],
  );

  // Local draft of the inputs; committed to PageContext (which drives the fetch) via Apply. All three
  // start empty/unset — an empty field sends no override, so the BE uses the deck's own cycle / year /
  // date. The admin only pins a value to preview a different one.
  const [deckCycle, setDeckCycle] = useState(overrides.deckCycle ?? '');
  const [publishYear, setPublishYear] = useState(overrides.publishYear ?? DECK_DEFAULT_YEAR);
  const [asOfDate, setAsOfDate] = useState(overrides.asOfDate ?? '');

  // Keep the draft in sync when the applied overrides change (Apply / Reset).
  useEffect(() => {
    setDeckCycle(overrides.deckCycle ?? '');
    setPublishYear(overrides.publishYear ?? DECK_DEFAULT_YEAR);
    setAsOfDate(overrides.asOfDate ?? '');
  }, [overrides.deckCycle, overrides.publishYear, overrides.asOfDate]);

  // Read the resolved cycle fields back off the same layout query Page.tsx issues (same key = cache hit).
  const { data: layout } = useGetPageLayout({
    pageId: currentGranularityPage?.pk ? entityKeyAsId(currentGranularityPage.pk) : undefined,
    deckCycle: overrides.deckCycle,
    publishYear: overrides.publishYear,
    asOfDate: overrides.asOfDate,
    options: { enabled: !isNil(currentGranularityPage?.pk) },
  });

  /**
   * Commits the current draft inputs to the applied overrides, triggering a layout re-fetch.
   */
  const handleApply = () => {
    const next: PageLayoutOverrides = {
      deckCycle: normalizeField(deckCycle),
      publishYear: normalizeField(publishYear),
      asOfDate: normalizeField(asOfDate),
    };
    onOverridesChange(next);
  };

  /**
   * Clears all overrides, returning the page to the deck's own cycle / year / date. Resets the local
   * draft directly so the inputs clear even when nothing was applied (the sync effect only fires when
   * the applied overrides actually change).
   */
  const handleReset = () => {
    setDeckCycle('');
    setPublishYear(DECK_DEFAULT_YEAR);
    setAsOfDate('');
    onOverridesChange({});
  };

  return (
    <Stack className='page-admin-panel'>
      <Typography variant='body2' className='page-admin-panel__caption'>
        Preview the page as another deck cycle, year, or date would resolve it.
      </Typography>

      <Autocomplete
        className='page-admin-panel__field'
        size='small'
        freeSolo
        options={deckCycleOptions}
        value={deckCycle}
        // freeSolo emits (event, value) for selection and onInputChange for typing; sync both so a
        // typed-but-not-selected cycle (e.g. an OP cycle not in the options) is still captured.
        onChange={(_event, value) => setDeckCycle(value ?? '')}
        onInputChange={(_event, value) => setDeckCycle(value)}
        renderInput={(params) => <TextField {...params} label='Deck Cycle' placeholder='e.g. Day7, Day12' />}
      />
      <FormControl className='page-admin-panel__field' size='small' fullWidth>
        <InputLabel id='page-admin-publish-year-label' shrink>
          Publish Year
        </InputLabel>
        <Select
          labelId='page-admin-publish-year-label'
          label='Publish Year'
          value={publishYear}
          displayEmpty
          notched
          onChange={(event) => setPublishYear(event.target.value)}
        >
          <MenuItem value={DECK_DEFAULT_YEAR}>
            <em>Deck default</em>
          </MenuItem>
          {publishYearOptions.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        className='page-admin-panel__field'
        size='small'
        fullWidth
        type='date'
        label='As Of Date'
        // A native date input always shows a placeholder, so the label must stay shrunk to avoid
        // overlapping it (same reason the Publish Year label is shrunk). Empty value = send nothing,
        // so the BE anchors on "now" — we only override the reporting date when a date is picked.
        slotProps={{ inputLabel: { shrink: true } }}
        value={asOfDate}
        onChange={(event) => setAsOfDate(event.target.value)}
      />

      <Stack direction='row' className='page-admin-panel__actions'>
        <Button variant='contained' size='small' onClick={handleApply}>
          Apply
        </Button>
        <Button variant='outlined' size='small' onClick={handleReset}>
          Reset
        </Button>
      </Stack>

      <List className='segments page-admin-panel__resolved'>
        <ListItem disablePadding>
          <ListItemText
            primary='Resolved Deck Cycle'
            secondary={layout?.resolvedDeckCycle ?? '—'}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
        <ListItem disablePadding>
          <ListItemText
            primary='Resolved Template Cycle'
            secondary={layout?.resolvedTemplateCycle ?? '—'}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    </Stack>
  );
};

export default PageAdminPanel;
